import type { ReactNode } from "react";
import { HttpError } from "../lib/api";

// Small primitives, hand-rolled. A component library would be a few hundred
// kilobytes for a dozen elements this app can define in a few hundred bytes.

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-hairline bg-raised shadow-[0_1px_2px_rgb(0_0_0/0.03)] ${className}`}>
      {children}
    </div>
  );
}

export function Button({
  children, onClick, type = "button", variant = "primary", disabled, size = "md", className = "",
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "primary" | "ghost" | "danger" | "quiet";
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-card font-medium transition disabled:opacity-40 disabled:pointer-events-none whitespace-nowrap";
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm" }[size];
  const variants = {
    primary: "bg-ink text-paper hover:opacity-85",
    ghost: "border border-hairline hover:bg-hairline/40",
    danger: "border border-rust/40 text-rust hover:bg-rust/10",
    quiet: "text-muted hover:text-ink hover:bg-hairline/40",
  }[variant];
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${sizes} ${variants} ${className}`}>
      {children}
    </button>
  );
}

export function Input({
  value, onChange, placeholder, type = "text", id, autoFocus, mono, className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  id?: string;
  autoFocus?: boolean;
  mono?: boolean;
  className?: string;
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded-card border border-hairline bg-paper px-3 py-2.5 text-sm outline-none transition focus:border-clay ${
        mono ? "font-mono" : ""
      } ${className}`}
    />
  );
}

export function Textarea({
  value, onChange, placeholder, rows = 5, autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  autoFocus?: boolean;
}) {
  return (
    <textarea
      rows={rows}
      value={value}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full resize-y rounded-card border border-hairline bg-paper px-3 py-2 text-sm leading-relaxed outline-none transition focus:border-clay"
    />
  );
}

/** Status pills. The colour vocabulary is fixed: moss = good/live, rust =
 *  failed, clay = in progress, muted = inert. Nothing else gets colour, so a
 *  coloured thing on the page always means something. */
export function Status({ value }: { value: string }) {
  const tone =
    value === "live" || value === "passed"
      ? "border-moss/40 text-moss bg-moss/10"
      : value === "failed" || value === "rolled_back"
      ? "border-rust/40 text-rust bg-rust/10"
      : ["building", "queued", "scaffolding", "filling", "verifying", "deploying", "shipping", "planning"].includes(value)
      ? "border-clay/40 text-clay bg-clay/10"
      : "border-hairline text-muted";

  const running = ["building", "queued", "scaffolding", "filling", "verifying", "deploying", "shipping"].includes(value);

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-2xs font-medium ${tone}`}>
      {running && <span className="live-dot h-1.5 w-1.5 rounded-full bg-clay" />}
      {value === "live" && <span className="live-dot h-1.5 w-1.5 rounded-full bg-moss" />}
      {value.replace(/_/g, " ")}
    </span>
  );
}

export function Badge({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "clay" }) {
  const cls = tone === "clay" ? "border-clay/40 text-clay" : "border-hairline text-muted";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-2xs ${cls}`}>
      {children}
    </span>
  );
}

export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-card border border-dashed border-hairline py-16 text-center">
      <p className="text-sm font-medium">{title}</p>
      {hint && <p className="max-w-md text-sm text-muted">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Renders an error the way the person reading it needs it: the server's own
 *  message, plus a hint for the two 402 codes that mean different things. */
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : "Something went wrong.";
  const code = error instanceof HttpError ? error.code : "";
  return (
    <div className="rounded-card border border-rust/30 bg-rust/5 px-4 py-3 text-sm text-rust">
      <p>{message}</p>
      {code === "insufficient_credits" && (
        <p className="mt-1 text-xs opacity-80">Top up on the Billing page to continue.</p>
      )}
      {code === "quota_exceeded" && (
        <p className="mt-1 text-xs opacity-80">Archive a project, or take an app down, to free a slot.</p>
      )}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-hairline border-t-clay" />
      {label && <p className="text-xs text-muted">{label}</p>}
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium">{label}</span>
      {children}
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </label>
  );
}

/** A multi-select rendered as toggle chips. Used for surfaces and features,
 *  where the whole point is seeing every option at once and what each costs. */
export function ChipGroup({
  options, selected, onToggle, disabled,
}: {
  options: { key: string; label: string; description?: string }[];
  selected: string[];
  onToggle: (key: string) => void;
  disabled?: (key: string) => boolean;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((o) => {
        const on = selected.includes(o.key);
        const off = disabled?.(o.key) ?? false;
        return (
          <button
            key={o.key}
            type="button"
            disabled={off}
            onClick={() => onToggle(o.key)}
            className={`rounded-card border px-3 py-2.5 text-left transition disabled:opacity-40 ${
              on ? "border-ink bg-ink text-paper" : "border-hairline hover:border-clay/60"
            }`}
          >
            <span className="block text-sm font-medium capitalize">{o.label}</span>
            {o.description && (
              <span className={`mt-0.5 block text-xs leading-snug ${on ? "opacity-70" : "text-muted"}`}>
                {o.description}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted">{sub}</p>}
    </Card>
  );
}

/** A banner for a rail running in simulation. Prominent on purpose: a
 *  simulated purchase or a simulated deploy that looked real is the worst
 *  thing this console could get wrong. */
export function SimulatedNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-card border border-clay/40 bg-clay/10 px-4 py-3 text-sm text-clay">
      {children}
    </div>
  );
}
